import { supabase } from './supabase';

interface Message {
  sender: string;
  content: string;
}

export const generateChatResponse = async (messages: Message[], previousHistory: Message[] = []): Promise<string> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-chat-response`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
    };

    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    console.log('[Debug] Calling edge function:', apiUrl);
    console.log('[Debug] Sending', messages.length, 'messages');

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ messages, previousHistory }),
    });

    console.log('[Debug] Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Debug] Error response:', errorText);
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || "I'm having trouble responding right now. Please try again in a moment.");
      } catch (parseError) {
        throw new Error("I'm having trouble responding right now. Please try again in a moment.");
      }
    }

    const data = await response.json();
    console.log('[Debug] Received response:', data.response?.substring(0, 50) + '...');
    return data.response || "I'm here to listen. Would you like to tell me more?";
  } catch (error) {
    console.error('Error generating chat response:', error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("I'm having trouble responding right now. Please try again in a moment.");
  }
};

export const generateTakeaway = async (messages: Message[]): Promise<string> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-takeaway`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
    };

    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ messages }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || "I'm having trouble creating your reflection right now. Please try again in a moment.");
    }

    const data = await response.json();
    return data.response ||
      "Today, we explored some important thoughts and feelings together.\n\n" +
      "I noticed how you opened up about what's on your mind, and I want you to know that your feelings are valid and understood.\n\n" +
      "Remember that sharing and reflecting on our thoughts is a sign of strength, and every step forward matters.";
  } catch (error) {
    console.error('Error generating reflection:', error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("I'm having trouble creating your reflection right now. Please try again in a moment.");
  }
};

export const getWelcomeMessage = async (): Promise<string> => {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-welcome-message`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
    };

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      throw new Error("Error fetching welcome message");
    }

    const data = await response.json();
    return data.response || "Hi, I'm Echo. I'm here whenever you need space to talk — no judgement, no pressure, just support. When you're done with the conversation, simply close the chat and your reflection will be saved automatically in your dashboard. Feel free to share what's on your mind anytime!";
  } catch (error) {
    console.error('Error generating welcome message:', error);

    // For welcome messages, we'll just return the default instead of throwing
    // since this is called during initialization
    return "Hi, I'm Echo. I'm here whenever you need space to talk — no judgement, no pressure, just support. When you're done with the conversation, simply close the chat and your reflection will be saved automatically in your dashboard. Feel free to share what's on your mind anytime!";
  }
};